"use client";

import { createContext } from "react";
import type { DataFieldValue } from "@huub-web/ui";
import type {
  FetchConfig,
  UIContextFormSlice,
  UIContextPageActionsPayload,
  UIContextPageSlice,
  UIContextPayload,
  UIContextSlice,
  UIContextSliceInput,
} from "@huub-web/backend";

import type { TemplateCollection } from "../types";

export type {
  UIContextFormSlice,
  UIContextPageActionsPayload,
  UIContextPageSlice,
  UIContextPayload,
  UIContextSlice,
  UIContextSliceInput,
};

export type BaseDataFieldResult = {
  success: boolean;
  error?: string;
};

export type DataFieldUpdateResult = BaseDataFieldResult & {
  value: DataFieldValue;
};

export type TemplatesProviderState = {
  /**
   * If the debug mode is enabled, the UI will be in debug mode.
   */
  debug: boolean;

  /**
   * Environment variables for the templates to use.
   */
  env: {
    mapBoxToken: string;
    /** Cookie name for cross-domain data sharing */
    cookieName?: string;
    /** Cookie domain for cross-subdomain sharing (e.g., .huub.nl) */
    cookieDomain?: string;
  };

  /**
   * If the agent is waiting for a response, or currently talking, other prompt inputs should be disabled.
   */
  lockPrompts: boolean;
  /**
   * If any enpoint is being called and is loading, the UI should be locked.
   */
  lockUI: boolean;

  dataFieldValues: Record<string, { value: DataFieldValue; label?: string }>;
  updateDataFieldValue: (
    endpoint: string,
    value: DataFieldValue,
    linkedFields?: string[],
  ) => Promise<DataFieldUpdateResult>;
  registerDataFieldValue: (
    id: string,
    value: DataFieldValue,
    label?: string,
  ) => void;

  uploadDocument: (
    endpoint: string,
    file: File,
  ) => Promise<BaseDataFieldResult>;
  deleteDocument: (
    endpoint: string,
    id: string,
  ) => Promise<BaseDataFieldResult>;
  downloadDocument: (endpoint: string) => Promise<void>;

  fetchTemplates: (endpoint: string) => Promise<TemplateCollection>;

  navigateToPath: (path: string) => void;

  /**
   * Register a UI context slice (form) by source ID.
   * The provider aggregates all slices and sends one combined WS message.
   */
  registerUIContext?: (id: string, slice: UIContextSliceInput) => void;
  /**
   * Remove a previously registered UI context slice (e.g. on unmount).
   */
  deregisterUIContext?: (id: string) => void;

  /**
   * Set page-level actions and content for the current page.
   * Owned by a single PageContentProvider; sent as a top-level
   * field in the WS payload, separate from slices.
   */
  setPageActions?: (payload: UIContextPageActionsPayload) => void;
  /**
   * Clear page-level actions and content (e.g. on page unmount).
   */
  clearPageActions?: () => void;

  /**
   * Set the current navigation context (e.g. ["assessment", "income"]).
   * Included in every unified ui-context WS message.
   */
  setNavigationContext?: (context: string[]) => void;
  /**
   * Set whether the chat side-panel is currently open.
   * Included in every unified ui-context WS message.
   */
  setChatOpen?: (open: boolean) => void;

  /**
   * Record that the user has explicitly confirmed a destructive page action
   * out-of-band (via a real UI gesture, e.g. the delete-confirmation modal).
   * The action name (the page action's `label`) is included in the
   * `confirmedActions` field of the unified ui-context WS message so the
   * backend's `trigger_page_action` tool can authorize it (SMA-1539). This is
   * the ONLY channel by which a destructive action becomes confirmed — the
   * agent cannot self-confirm.
   */
  confirmAction?: (actionName: string) => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $post: <T = any>(
    endpoint: string,
    body: Record<string, string | boolean | number | null>,
    config?: FetchConfig,
  ) => Promise<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $get: <T = any>(endpoint: string, config?: FetchConfig) => Promise<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $delete: <T = any>(endpoint: string, config?: FetchConfig) => Promise<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $put: <T = any>(
    endpoint: string,
    body: Record<string, string | boolean | number | null>,
    config?: FetchConfig,
  ) => Promise<T>;
};

export const TemplatesProviderContext = createContext<
  TemplatesProviderState | undefined
>(undefined);
